// @ts-strict-ignore
import {ProjectFeatureFlag, REPORT_LIMITS, ReportName} from 'common/report/constants';
import {CurrentProject} from 'common/report/model-resources/projects';
import {computeMetricClauseByKey, createMetricUnitLabeler} from 'common/report/insights/util/tooltip';
import {FlatSegment, InsightsQueryResults, ReportSectionsMetadata} from 'common/report/insights/util/types';
import {ChartType, InsightsBookmarkParams} from 'common/types/reports/bookmark';
import {
  getFlatSegments,
  getSegmentLookupMap,
  getSingleNumberFromSegmentValue,
  getSortedKeyMapForFlatSegments,
  isSingleSegmentedMetric,
} from 'common/report/insights/util';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {MetricSegment} from 'common/charts/visualization/multi-metric/types';
import {getLiftBaselineSegment} from 'common/report/util/lift';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {Translatable} from 'common/i18n';
import {LocalizationStore} from 'common/i18n/store';

export interface InsightsMetricLabels {
  metricName: string;
  metricUnit?: Translatable;
}

export interface InsightsMetricData {
  segmentLookup: Map<string, FlatSegment>;
  timeComparisonLookup: Map<string, FlatSegment>;
  liftComparisonLookup: Map<string, FlatSegment>;

  // Insights uses a special index with a stable sort order (independent from the bookmark sort order) to store selected legend segments
  legendIdToIndexMap: {[id: string]: number};
  legendIndexToId: string[];
  /** holds positioning of the popover for the active highcharts point */
  popoverStyles?: {
    visibility?: string;
    left?: string;
    top?: string;
  } | null;
  popoverSegment?: MetricSegment | null;
}

export function getMetricData(
  results: InsightsQueryResults,
  bookmarkParams: InsightsBookmarkParams,
): InsightsMetricData {
  const segments = getFlatSegments({
    headers: results.headers,
    resultSeries: results.series,
    groupClauses: bookmarkParams.sections.group,
    secondaryResultSeries: results.secondary?.series,
  });
  const segmentLookup = getSegmentLookupMap(segments);
  let timeComparisonLookup = null;
  if (results.time_comparison) {
    const timeComparisonSegments = getFlatSegments({
      headers: results.headers,
      resultSeries: results.time_comparison.series,
      groupClauses: bookmarkParams.sections.group,
      secondaryResultSeries: results.secondary_time_comparison?.series,
    });
    timeComparisonLookup = getSegmentLookupMap(timeComparisonSegments);
  }

  let liftComparisonLookup = null;
  if (results.lift_comparison) {
    const liftComparisonSegments = getFlatSegments({
      headers: results.headers,
      resultSeries: results.lift_comparison.series,
      groupClauses: bookmarkParams.sections.group,
    });
    liftComparisonLookup = getSegmentLookupMap(liftComparisonSegments);
  }

  const legendIdToIndexMap = getSortedKeyMapForFlatSegments(segments);

  const legendIndexToId: string[] = [];
  Object.keys(legendIdToIndexMap).forEach((id) => (legendIndexToId[legendIdToIndexMap[id]] = id));

  return {segmentLookup, legendIdToIndexMap, legendIndexToId, timeComparisonLookup, liftComparisonLookup};
}

export function getMetricLabels({
  bookmarkParams,
  sectionsMetadata,
  dataGroups,
  count,
  key,
}: {
  bookmarkParams: InsightsBookmarkParams;
  sectionsMetadata: ReportSectionsMetadata;
  dataGroups: DataGroup[];
  count: number;
  key: string;
}): InsightsMetricLabels {
  const metricClauseByKey = computeMetricClauseByKey({
    sectionsMetadata,
    bookmarkParams,
    dataGroups,
  });

  const metricClause = metricClauseByKey[key];
  const metricName = metricClause.metricKey;

  const metricUnitLabeler = createMetricUnitLabeler(metricClause);
  const metricUnit: Translatable = metricUnitLabeler ? metricUnitLabeler(count) : ``;

  return {metricName, metricUnit};
}

// TODO: Remove this flag and function after top n option is exposed in product
export function getDefaultSelectedSegmentLimit(currentProject: CurrentProject) {
  return currentProject?.hasFeatureFlag(ProjectFeatureFlag.InsightsMetricOverrideDefaultSelected)
    ? 10
    : REPORT_LIMITS[ReportName.Insights].segments[ChartType.Metric].default;
}

export function getSegmentMetricData({
  segmentFilterController,
  segmentLookup,
  timeComparisonLookup,
  liftComparisonLookup,
  segmentId,
  bookmarkParams,
  results,
  dataGroups,
  i18n,
}: {
  segmentFilterController: SegmentFilterController;
  segmentLookup: Map<string, FlatSegment>;
  timeComparisonLookup: Map<string, FlatSegment>;
  liftComparisonLookup: Map<string, FlatSegment>;
  segmentId: string;
  bookmarkParams: InsightsBookmarkParams;
  results: InsightsQueryResults;
  dataGroups: Record<string, DataGroup>;
  i18n: LocalizationStore;
}): MetricSegment {
  const segment = segmentLookup.get(segmentId);
  const value = getSingleNumberFromSegmentValue(segment.value);
  const comparisonSegment = timeComparisonLookup?.get(segment.key);
  let comparisonValue = comparisonSegment ? getSingleNumberFromSegmentValue(comparisonSegment.value) : null;
  let liftComparisonValue;
  if (bookmarkParams.liftComparison) {
    const liftComparisonSegment = liftComparisonLookup?.get(segment.key);
    liftComparisonValue = liftComparisonSegment ? getSingleNumberFromSegmentValue(liftComparisonSegment.value) : null;

    const baselineSegment = getLiftBaselineSegment(
      bookmarkParams.liftComparison,
      segment.path,
      segmentLookup,
      comparisonSegment,
    );
    comparisonValue = baselineSegment ? getSingleNumberFromSegmentValue(baselineSegment.value) : 0;
  }

  const {metricUnit} = getMetricLabels({
    bookmarkParams,
    sectionsMetadata: results.meta.report_sections,
    dataGroups: Object.values(dataGroups),
    count: value,
    key: segment.path[0],
  });

  const secondaryValue = segment.secondaryValue ? getSingleNumberFromSegmentValue(segment.secondaryValue) : null;

  return {
    value,
    secondaryValue,
    path: segment.path,
    comparisonValue,
    liftComparisonValue,
    label: isSingleSegmentedMetric(results.headers, results.series)
      ? segment.displayNamePath.slice(1).join(` / `)
      : segment.displayName,
    subLabel: i18n.print(metricUnit),
    color: segmentFilterController.getItemColor(0, segmentId),
  };
}
